import React, { useContext, useEffect, useState } from "react"
import Context from "../../../contexts/Context"
import { getCondoId } from "../../../services/Main/AuthStorage"
import { api } from "../../../services/Main/Api"
import { Helmet } from "react-helmet"
import {
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material"
import SearchInput from "../../../components/Filters/SearchInput"
import NoResultsAnimation from "../../../components/Shared/animations/NoResultsAnimation"
import PaginationGlobal from "../../../components/Shared/PaginationGlobal"
import CircularProgress from "@mui/material/CircularProgress"
import Divider from "@mui/material/Divider"
import { Add, Delete, PictureAsPdf } from "@mui/icons-material"
import { useNavigate } from "react-router-dom"
import { useSnackbar } from "notistack"
import moment from "moment/moment"
import InspectionForm from "./InspectionForm"
import InspectionStatusChip from "./components/InspectionStatusChip"
import InspectionTypeChip from "./components/InspectionTypeChip"

const InspectionList = () => {
  const { filters, setFilters } = useContext(Context)
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  const [data, setData] = useState(null)
  const [total, setTotal] = useState(0)
  const [openForm, setOpenForm] = useState(false)
  const [reqLoading, setReqLoading] = useState(false)
  const [typeFilter, setTypeFilter] = useState("")
  const [statusFilter, setStatusFilter] = useState("")

  function initialFilters() {
    return setFilters((prevState) => ({
      ...prevState,
      page: 1,
      page_size: 50,
      search: "",
      ordering: "-id",
    }))
  }

  useEffect(function filterSet() {
    initialFilters()
  }, [])

  function listInspections() {
    const condoId = getCondoId()
    if (!condoId) return

    const params = new URLSearchParams()
    params.append("condo_id", condoId)
    params.append("page", filters?.page)
    params.append("page_size", filters?.page_size)
    if (filters?.search) params.append("search", filters.search)
    if (filters?.ordering) params.append("ordering", filters.ordering)
    if (typeFilter) params.append("type_inspection", typeFilter)
    if (statusFilter) params.append("status", statusFilter)

    api
      .get(`/property-inspection/`, { params })
      .then((response) => {
        setData(response.data)
        setTotal(response.data.count)
      })
      .catch((error) => {
        console.log(error)
      })
  }

  useEffect(
    function firstLoad() {
      if (filters) {
        listInspections()
      }
    },
    [filters, typeFilter, statusFilter]
  )

  const handleFilter = (asc, desc) => {
    setFilters((prevState) => ({
      ...prevState,
      ordering: filters?.ordering === asc ? desc : asc,
    }))
  }

  function handleDelete(e, id) {
    e.stopPropagation()
    if (!window.confirm("Deseja realmente excluir esta vistoria?")) return
    setReqLoading(true)
    api
      .delete(`/property-inspection/${id}/`)
      .then(() => {
        enqueueSnackbar("Vistoria excluída com sucesso!", { variant: "success" })
        listInspections()
      })
      .catch((error) => {
        enqueueSnackbar("Erro ao excluir vistoria.", { variant: "error" })
        console.log(error)
      })
      .finally(() => setReqLoading(false))
  }

  return (
    <>
      <Helmet>
        <title>Vistorias - Bloco B3</title>
      </Helmet>

      {openForm && (
        <InspectionForm
          closeForm={() => setOpenForm(false)}
          onCreated={(id) => navigate(`/vistoria/${id}`)}
        />
      )}

      <Backdrop sx={{ color: "#fff", zIndex: 1500 }} open={reqLoading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Container maxWidth={false}>
        <Box
          sx={{
            display: "flex",
            "@media (max-width:600px)": {
              display: "grid",
              gridTemplateColumns: "repeat(1, 1fr)",
              gap: 3,
            },
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography textAlign={"center"} variant={"h5"}>
            Vistorias ({total})
          </Typography>

          <Button
            variant={"contained"}
            size={"small"}
            onClick={() => setOpenForm(true)}
          >
            <Add /> Criar Vistoria
          </Button>
        </Box>

        <Card>
          {data ? (
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <SearchInput label="Buscar vistoria" />
                </Grid>
                <Grid item xs={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Tipo</InputLabel>
                    <Select
                      value={typeFilter}
                      label="Tipo"
                      onChange={(e) => setTypeFilter(e.target.value)}
                    >
                      <MenuItem value="">Todos</MenuItem>
                      <MenuItem value="initial">Entrada</MenuItem>
                      <MenuItem value="exit">Saída</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      label="Status"
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <MenuItem value="">Todos</MenuItem>
                      <MenuItem value="open">Aberto</MenuItem>
                      <MenuItem value="file">Gerando Laudo</MenuItem>
                      <MenuItem value="waiting">Aguardando Assinatura</MenuItem>
                      <MenuItem value="signed">Assinado</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <TableContainer>
                {data?.results?.length > 0 ? (
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <TableSortLabel
                            style={{ fontSize: "14px" }}
                            active={
                              filters?.ordering === "-id" ||
                              filters?.ordering === "id"
                            }
                            direction={
                              filters?.ordering === "-id" ? "desc" : "asc"
                            }
                            onClick={() => handleFilter("id", "-id")}
                          >
                            Nº
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            style={{ fontSize: "14px" }}
                            active={
                              filters?.ordering === "-name" ||
                              filters?.ordering === "name"
                            }
                            direction={
                              filters?.ordering === "-name" ? "desc" : "asc"
                            }
                            onClick={() => handleFilter("name", "-name")}
                          >
                            Nome
                          </TableSortLabel>
                        </TableCell>
                        <TableCell style={{ fontSize: "14px" }}>
                          Endereço
                        </TableCell>
                        <TableCell align="center" style={{ fontSize: "14px" }}>
                          Tipo
                        </TableCell>
                        <TableCell align="center">
                          <TableSortLabel
                            style={{ fontSize: "14px" }}
                            active={
                              filters?.ordering === "-status" ||
                              filters?.ordering === "status"
                            }
                            direction={
                              filters?.ordering === "-status" ? "desc" : "asc"
                            }
                            onClick={() => handleFilter("status", "-status")}
                          >
                            Status
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="center">
                          <TableSortLabel
                            style={{ fontSize: "14px" }}
                            active={
                              filters?.ordering === "-created_at" ||
                              filters?.ordering === "created_at"
                            }
                            direction={
                              filters?.ordering === "-created_at"
                                ? "desc"
                                : "asc"
                            }
                            onClick={() =>
                              handleFilter("created_at", "-created_at")
                            }
                          >
                            Criado em
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="center" style={{ fontSize: "14px" }}>
                          Laudo
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.results.map((item) => (
                        <TableRow
                          key={item.id}
                          hover
                          sx={{ cursor: "pointer" }}
                          onClick={() => navigate(`/vistoria/${item.id}`)}
                        >
                          <TableCell>{item.number}</TableCell>
                          <TableCell>{item.name}</TableCell>
                          <TableCell
                            sx={{
                              maxWidth: 200,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <Tooltip title={item.full_address || ""}>
                              <span>{item.full_address || "—"}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="center">
                            <InspectionTypeChip type={item.type_inspection} />
                          </TableCell>
                          <TableCell align="center">
                            <InspectionStatusChip status={item.status} />
                          </TableCell>
                          <TableCell align="center">
                            {moment(item.created_at).format("DD/MM/YYYY")}
                          </TableCell>
                          <TableCell align="center">
                            {item.file ? (
                              <Tooltip title="Baixar Laudo PDF">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    window.open(item.file, "_blank")
                                  }}
                                >
                                  <PictureAsPdf />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              "—"
                            )}
                          </TableCell>
                          <TableCell>
                            {item.status === "open" && (
                              <Tooltip title="Excluir">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={(e) => handleDelete(e, item.id)}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <NoResultsAnimation />
                )}
              </TableContainer>
            </CardContent>
          ) : (
            <Skeleton variant="rounded" height={700} animation="pulse" />
          )}
        </Card>

        <PaginationGlobal
          state={data}
          filters={filters}
          setFilters={setFilters}
        />
      </Container>
    </>
  )
}

export default InspectionList
